import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import Navbar from "../../../components/navbar";
import Footer from "../../../components/footer";
import ScrollToTop from "../../../components/scroll-to-top";
import toast from "react-hot-toast";

const CheckCircleIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="text-green-500"
  >
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
    <polyline points="22 4 12 14.01 9 11.01"></polyline>
  </svg>
);

const PackageIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="text-blue-500"
  >
    <line x1="16.5" y1="9.4" x2="7.5" y2="4.21"></line>
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
    <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
    <line x1="12" y1="22.08" x2="12" y2="12"></line>
  </svg>
);

const TruckIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="text-green-500"
  >
    <path d="M1 3h15v13H1z"></path>
    <path d="M16 8h4l3 3v5h-7V8z"></path>
    <circle cx="5.5" cy="18.5" r="2.5"></circle>
    <circle cx="18.5" cy="18.5" r="2.5"></circle>
  </svg>
);

const CreditCardIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="text-purple-500"
  >
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
    <line x1="1" y1="10" x2="23" y2="10"></line>
  </svg>
);

const OrderVerificationSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [orderData, setOrderData] = useState(null);
  const [isProduct, setIsProduct] = useState(true);
  const [loading, setLoading] = useState(true);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [verifyingPayment, setVerifyingPayment] = useState(false);

  useEffect(() => {
    const checkPaymentResponse = async () => {
      // Check if we have URL parameters from PhonePe redirect
      const transactionId = searchParams.get("transactionId");
      const orderId = searchParams.get("orderId");
      const status = searchParams.get("status");
      const amount = searchParams.get("amount");
      const paymentMode = searchParams.get("paymentMode");
      
      // Get order ID from localStorage as fallback
      const storedOrderId = localStorage.getItem("orderId");
      
      // Determine which order ID to use (prioritize URL parameters)
      const orderToVerify = orderId || storedOrderId;
      
      // If we have order data in location state (for COD orders)
      if (location.state && location.state.orderData) {
        setOrderData(location.state.orderData);
        setIsProduct(location.state.is_product !== undefined ? location.state.is_product : true);
        setLoading(false);
      } 
      // If we have an order ID to verify (either from URL or localStorage)
      else if (orderToVerify) {
        setVerifyingPayment(true);
        
        try {
          // Make single API call to verify payment status with our backend
          const response = await axios.post(
            `${process.env.REACT_APP_API_BASE_URL}/verify-payment/`,
            { order_id: orderToVerify }
          );
          
          if (response.data) {
            // Set order data from the response
            setOrderData(response.data);
            setIsProduct(true); // Assume product order for PhonePe payments
            
            // Set payment details from the API response
            setPaymentDetails({
              transactionId: response.data.transaction_id,
              orderId: response.data.order_id,
              status: response.data.status,
              amount: response.data.amount_paid,
              paymentMode: "PhonePe",
              message: response.data.message,
              expected_delivery_date: response.data.expected_delivery_date
            });
          }
        } catch (error) {
          console.error("Error verifying payment:", error);
          toast.error("Failed to verify payment status", {
            style: { borderRadius: "10px", background: "#333", color: "#fff" },
          });
        } finally {
          setVerifyingPayment(false);
          setLoading(false);
        }
      }
      // If no order data is available
      else {
        setLoading(false);
      }
    };

    checkPaymentResponse();
  }, [location, navigate, searchParams]);

  const formatDeliveryDate = (dateString) => {
    if (!dateString) return "Not available";
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (error) {
      return dateString;
    }
  };

  // Add this function to format currency
  const formatCurrency = (amount) => {
    if (!amount) return "₹0";
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <>
        <Navbar navClass="defaultscroll is-sticky" navlight={true} />
        <section className="relative md:py-24 py-16">
          <div className="container relative">
            <div className="max-w-3xl mx-auto">
              <div className="p-8 rounded-md shadow dark:shadow-gray-800 bg-white dark:bg-slate-900">
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <Footer />
        <ScrollToTop />
      </>
    );
  }

  if (!orderData && !paymentDetails) {
    return (
      <>
        <Navbar navClass="defaultscroll is-sticky" navlight={true} />
        <section className="relative md:py-24 py-16">
          <div className="container relative">
            <div className="max-w-3xl mx-auto">
              <div className="p-8 rounded-md shadow dark:shadow-gray-800 bg-white dark:bg-slate-900 text-center">
                <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">
                  Order Information Not Found
                </h2>
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  We couldn't find your order information. Please check your email for order confirmation or contact support.
                </p>
                <button
                  onClick={() => navigate("/")}
                  className="px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors"
                >
                  Return to Home
                </button>
              </div>
            </div>
          </div>
        </section>
        <Footer />
        <ScrollToTop />
      </>
    );
  }

  return (
    <>
      <Navbar navClass="defaultscroll is-sticky" navlight={true} />
      
      <section className="relative md:py-24 py-16">
        <div className="container relative">
          <div className="max-w-4xl mx-auto">
            <div className="p-8 rounded-md shadow dark:shadow-gray-800 bg-white dark:bg-slate-900">
              {verifyingPayment ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500 mx-auto mb-4"></div>
                  <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-2">
                    Verifying Payment
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    Please wait while we verify your payment status...
                  </p>
                </div>
              ) : paymentDetails ? (
                // Payment status section for PhonePe orders
                <div className="text-center mb-8">
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
                    paymentDetails.status === "Success" ? "bg-green-100 text-green-500" : "bg-red-100 text-red-500"
                  }`}>
                    {paymentDetails.status === "Success" ? (
                      <CheckCircleIcon />
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="15" y1="9" x2="9" y2="15"></line>
                        <line x1="9" y1="9" x2="15" y2="15"></line>
                      </svg>
                    )}
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
                    {paymentDetails.status === "Success" ? "Payment Successful!" : "Payment Failed"}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-300 mb-6">
                    {paymentDetails.message}
                  </p>
                  
                  {/* Payment Details Section */}
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-8 text-left">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                      Payment Details
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Transaction ID</p>
                        <p className="font-mono text-sm break-all">{paymentDetails.transactionId}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Order ID</p>
                        <p className="font-mono text-sm break-all">{paymentDetails.orderId}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Payment Status</p>
                        <p className={`font-medium ${
                          paymentDetails.status === "Success" ? "text-green-600" : "text-red-600"
                        }`}>
                          {paymentDetails.status}
                        </p>
                      </div>
                      {paymentDetails.amount && (
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">Amount Paid</p>
                          <p className="font-medium">{formatCurrency(paymentDetails.amount)}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Payment Mode</p>
                        <p className="font-medium">{paymentDetails.paymentMode}</p>
                      </div>
                      {paymentDetails.expected_delivery_date && (
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">Expected Delivery</p>
                          <p className="font-medium">{formatDeliveryDate(paymentDetails.expected_delivery_date)}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-4 pt-4">
                    {paymentDetails.status === "Success" ? (
                      <>
                        <button
                          onClick={() => navigate("/")}
                          className="flex-1 px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors"
                        >
                          Continue Shopping
                        </button>
                        <button
                          onClick={() => navigate("/orders")}
                          className="flex-1 px-6 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-800 dark:text-white rounded-lg font-medium transition-colors"
                        >
                          View Order Status
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => navigate("/checkout")}
                          className="flex-1 px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors"
                        >
                          Try Again
                        </button>
                        <button
                          onClick={() => navigate("/")}
                          className="flex-1 px-6 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-800 dark:text-white rounded-lg font-medium transition-colors"
                        >
                          Return to Home
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ) : orderData ? (
                // Original order confirmation section for COD orders
                <>
                  <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 text-green-500 mb-4">
                      <CheckCircleIcon />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
                      Order Confirmed!
                    </h2>
                    <p className="text-gray-600 dark:text-gray-300">
                      {orderData.message || "Thank you for your purchase. We've received your order and will begin processing it right away."}
                    </p>
                  </div>

                  {isProduct ? (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 text-center">
                          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 text-blue-500 mb-4">
                            <PackageIcon />
                          </div>
                          <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">
                            Order ID
                          </h3>
                          <p className="text-gray-600 dark:text-gray-300 font-mono">
                            {orderData.order_id}
                          </p>
                        </div>

                        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 text-center">
                          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-purple-100 text-purple-500 mb-4">
                            <CreditCardIcon />
                          </div>
                          <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">
                            Payment Method
                          </h3>
                          <p className="text-gray-600 dark:text-gray-300">
                            Cash on Delivery
                          </p>
                        </div>

                        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 text-center">
                          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 text-green-500 mb-4">
                            <TruckIcon />
                          </div>
                          <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">
                            Expected Delivery
                          </h3>
                          <p className="text-gray-600 dark:text-gray-300">
                            {formatDeliveryDate(orderData.expected_delivery_date)}
                          </p>
                        </div>
                      </div>

                      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 mb-8">
                        <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                          What's Next?
                        </h3>
                        <div className="space-y-4">
                          <div className="flex items-start">
                            <div className="flex-shrink-0 mt-1">
                              <div className="flex items-center justify-center h-6 w-6 rounded-full bg-blue-500 text-white text-xs">
                                1
                              </div>
                            </div>
                            <div className="ml-3">
                              <h4 className="text-md font-medium text-gray-800 dark:text-white">
                                Confirmation Email
                              </h4>
                              <p className="text-gray-600 dark:text-gray-300">
                                You'll receive an order confirmation email shortly with all the details.
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex items-start">
                            <div className="flex-shrink-0 mt-1">
                              <div className="flex items-center justify-center h-6 w-6 rounded-full bg-blue-500 text-white text-xs">
                                2
                              </div>
                            </div>
                            <div className="ml-3">
                              <h4 className="text-md font-medium text-gray-800 dark:text-white">
                                Order Processing
                              </h4>
                              <p className="text-gray-600 dark:text-gray-300">
                                We'll process your order within 1-2 business days.
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex items-start">
                            <div className="flex-shrink-0 mt-1">
                              <div className="flex items-center justify-center h-6 w-6 rounded-full bg-blue-500 text-white text-xs">
                                3
                              </div>
                            </div>
                            <div className="ml-3">
                              <h4 className="text-md font-medium text-gray-800 dark:text-white">
                                Shipping Updates
                              </h4>
                              <p className="text-gray-600 dark:text-gray-300">
                                You'll receive shipping updates via email and SMS once your order is on the way.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col sm:flex-row gap-4 pt-4">
                        <button
                          onClick={() => navigate("/")}
                          className="flex-1 px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors"
                        >
                          Continue Shopping
                        </button>
                        <button
                          onClick={() => navigate("/orders")}
                          className="flex-1 px-6 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-800 dark:text-white rounded-lg font-medium transition-colors"
                        >
                          View Order Status
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-8 mb-8 text-center">
                      <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">
                        Custom Order Processing
                      </h3>
                      <p className="text-gray-600 dark:text-gray-300 mb-6">
                        Thank you for your custom order! Our team will review your customization details and contact you soon. 
                        Please wait for a maximum of 24-48 hours for us to process your order and provide you with further updates.
                      </p>
                      <div className="flex justify-center">
                        <button
                          onClick={() => navigate("/")}
                          className="px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors"
                        >
                          Return to Home
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                // No order data available
                <div className="text-center py-12">
                  <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">
                    Order Information Not Found
                  </h2>
                  <p className="text-gray-600 dark:text-gray-300 mb-6">
                    We couldn't find your order information. Please check your email for order confirmation or contact support.
                  </p>
                  <button
                    onClick={() => navigate("/")}
                    className="px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors"
                  >
                    Return to Home
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <ScrollToTop />
    </>
  );
};

export default OrderVerificationSuccess;